import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { V2ActionsProvider } from './context/V2ActionsContext';
import { AuthProvider } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './Login';
import OAuthConsentPage from './pages/OAuthConsentPage';
import Portfolio from './Portfolio';
import SnapshotsDebug from './SnapshotsDebug';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import { ButtonDemo } from './components/ui-v2';
// V2 Pages (now the default UI)
import {
  Dashboard as V2Dashboard,
  Holdings as V2Holdings,
  Transactions as V2Transactions,
  CashFlow as V2CashFlow,
  Analytics as V2Analytics,
  Settings as V2Settings,
  Snapshots as V2Snapshots,
  Contributions as V2Contributions,
  Events as V2Events,
  Insights as V2Insights,
  AdminMetrics as V2AdminMetrics,
} from './pages/v2';
// Portfolio Story — post-login scroll summary, rendered full-bleed outside AppLayout
import Story from './pages/story/Story';
import './App.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          {/* Top-level boundary: catches any render error from the router or
              any route, preventing a single bad component from white-screening
              the entire SPA. Per-route boundaries below provide granular
              isolation so one broken page doesn't take down navigation. */}
          <ErrorBoundary scope="app">
            <Routes>
              <Route
                path="/login"
                element={
                  <ErrorBoundary scope="Login">
                    <Login />
                  </ErrorBoundary>
                }
              />

              {/* OAuth consent page (issue #257) — deliberately NOT wrapped in
                  ProtectedRoute: ProtectedRoute redirects to a bare /login and
                  drops the location, which would lose the pending authorize
                  request. OAuthConsentPage handles the logged-out case itself,
                  redirecting to /login?returnTo=<this path+query>. */}
              <Route
                path="/oauth/consent"
                element={
                  <ErrorBoundary scope="OAuth Consent">
                    <OAuthConsentPage />
                  </ErrorBoundary>
                }
              />

              {/* V1 Routes - Legacy UI (moved to /v1 prefix) */}
              <Route
                path="/v1"
                element={
                  <ErrorBoundary scope="Portfolio (v1)">
                    <ProtectedRoute>
                      <Portfolio />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/v1/analytics"
                element={
                  <ErrorBoundary scope="Analytics (v1)">
                    <ProtectedRoute>
                      <AnalyticsDashboard />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/v1/snapshots-debug"
                element={
                  <ErrorBoundary scope="Snapshots Debug (v1)">
                    <ProtectedRoute>
                      <SnapshotsDebug />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/ui-v2-demo"
                element={
                  <ErrorBoundary scope="UI v2 Demo">
                    <ProtectedRoute>
                      <ButtonDemo />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />

              {/* Portfolio Story — full-bleed post-login landing. Deliberately
                  outside V2ActionsProvider: the page is read-only and mounts no
                  dialogs, so it needs none of the v2 action plumbing. */}
              <Route
                path="/story"
                element={
                  <ErrorBoundary scope="Story">
                    <ProtectedRoute>
                      <Story />
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />

              {/* V2 Routes - Modern UI (now the default at root paths) */}
              <Route
                path="/"
                element={
                  <ErrorBoundary scope="Dashboard">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Dashboard />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/holdings/:type"
                element={
                  <ErrorBoundary scope="Holdings">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Holdings />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/transactions/:type"
                element={
                  <ErrorBoundary scope="Transactions">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Transactions />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/cash-flow/:type"
                element={
                  <ErrorBoundary scope="Cash Flow">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2CashFlow />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/analytics/:type"
                element={
                  <ErrorBoundary scope="Analytics">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Analytics />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/settings"
                element={
                  <ErrorBoundary scope="Settings">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Settings />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/snapshots"
                element={
                  <ErrorBoundary scope="Snapshots">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Snapshots />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/contributions"
                element={
                  <ErrorBoundary scope="Contributions">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Contributions />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/events"
                element={
                  <ErrorBoundary scope="Events">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Events />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/insights"
                element={
                  <ErrorBoundary scope="Insights">
                    <ProtectedRoute>
                      <V2ActionsProvider>
                        <V2Insights />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/admin/metrics"
                element={
                  <ErrorBoundary scope="Admin Metrics">
                    <ProtectedRoute guard={(user) => user?.isAdmin} fallback="/">
                      <V2ActionsProvider>
                        <V2AdminMetrics />
                      </V2ActionsProvider>
                    </ProtectedRoute>
                  </ErrorBoundary>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
